/**
 * Encode and decode QR payloads: every envelope printed since 2019, and the v1 forms.
 *
 * Decoding never rejects input. Anything that is not a recognised envelope comes back as a raw
 * value, because stored codes are opaque strings (adopted stickers, imported codes, free text).
 */

const libHosts = require('./Headlight-QRCode-Hosts.js');
const EntityRegistry = require('./Headlight-QRCode-EntityRegistry.js');

/**
 * @typedef {'Compact'|'Full'|'LegacyLinks'|'Raw'} Envelope
 */

/**
 * @typedef {Object} DecodedAction
 * @property {'Action'} Kind
 * @property {string} Workflow - e.g. `cart`, `receive`
 * @property {string} Verb - e.g. `open`, `submit`, `start`
 * @property {Array<string>} Arguments
 * @property {boolean} Legacy - True for the three codes printed before workflows were namespaced.
 * @property {string} Raw
 */

/**
 * @typedef {Object} DecodedEntity
 * @property {'Entity'} Kind
 * @property {string|null} Entity - Registry entity name, or null when the payload doesn't say (raw codes).
 * @property {'qr'|'guid'|'id'} Key
 * @property {string} Value
 * @property {1|'legacy'|'raw'} Version
 * @property {'Compact'|'Full'|'LegacyLinks'|'LegacyEntityLinks'|'LegacyDocumentLink'|'Raw'|'ForeignURL'} Shape
 * @property {string} [Environment] - Environment of the links host the label points at.
 * @property {string} Raw - The input, trimmed.
 */

/**
 * @typedef {Object} DecodedEmpty
 * @property {'Empty'} Kind
 * @property {string} Raw
 */

/** @typedef {DecodedAction|DecodedEntity|DecodedEmpty} Decoded */

/**
 * The three action codes printed before workflow namespacing, mapped to their workflow form.
 * New workflows must never be named `open`, `clear` or `submit`.
 */
const LEGACY_ACTIONS =
{
	'open/cart': { Workflow: 'cart', Verb: 'open' },
	'clear': { Workflow: 'cart', Verb: 'clear' },
	'submit': { Workflow: 'cart', Verb: 'submit' },
};
const LEGACY_ACTION_HEADS = [ 'open', 'clear', 'submit' ];

/** Characters QR alphanumeric mode can encode. */
const ALPHANUMERIC_MODE = /^[0-9A-Z $%*+\-./:]*$/;

/**
 * Strip whitespace and control characters scanners append or prepend (CR, LF, TAB, GS…).
 *
 * @param {string} pText
 * @return {string}
 */
function cleanScannedText(pText)
{
	// eslint-disable-next-line no-control-regex
	return String(pText).replace(/^[\s\u0000-\u001f\u007f]+|[\s\u0000-\u001f\u007f]+$/g, '');
}

/**
 * @param {string} pSegment
 * @return {string}
 */
function safeDecodeSegment(pSegment)
{
	try
	{
		return decodeURIComponent(pSegment);
	}
	catch (pError)
	{
		return pSegment;
	}
}

/**
 * @param {string} pText
 * @return {boolean} true when the whole string fits QR alphanumeric mode
 */
function isAlphanumericMode(pText)
{
	return ALPHANUMERIC_MODE.test(pText);
}

class QRCodeCodec
{
	/**
	 * @param {Object} [pOptions]
	 * @param {EntityRegistry} [pOptions.Registry]
	 */
	constructor(pOptions)
	{
		const tmpOptions = pOptions || {};
		/** @type {EntityRegistry} */
		this.registry = tmpOptions.Registry || new EntityRegistry();
	}

	/**
	 * Decode scanned or typed text.
	 *
	 * @param {any} pText
	 * @return {Decoded}
	 */
	decode(pText)
	{
		const tmpRaw = (pText === null || pText === undefined) ? '' : cleanScannedText(pText);
		if (!tmpRaw)
		{
			return { Kind: 'Empty', Raw: tmpRaw };
		}

		if (/^action\//i.test(tmpRaw))
		{
			return this._decodeActionSegments(tmpRaw.split('?')[0].split('/').slice(1), tmpRaw);
		}

		if (/^https?:\/\//i.test(tmpRaw))
		{
			return this._decodeURL(tmpRaw);
		}

		return this._raw(tmpRaw, 'Raw');
	}

	/**
	 * @param {string} pRaw
	 * @return {Decoded}
	 */
	_decodeURL(pRaw)
	{
		let tmpURL = null;
		try
		{
			tmpURL = new URL(pRaw);
		}
		catch (pError)
		{
			return this._raw(pRaw, 'Raw');
		}

		if (!libHosts.isLinksHostname(tmpURL.hostname))
		{
			return this._raw(pRaw, 'ForeignURL');
		}

		const tmpEnvironment = libHosts.parseHostname(tmpURL.hostname).Environment;
		const tmpSegments = tmpURL.pathname.split('/').filter((pSegment) => pSegment.length > 0).map(safeDecodeSegment);
		if (tmpSegments.length === 0)
		{
			return this._raw(pRaw, 'ForeignURL');
		}

		const tmpHead = tmpSegments[0].toLowerCase();

		if (tmpHead === 'action')
		{
			return this._decodeActionSegments(tmpSegments.slice(1), pRaw);
		}

		if (tmpHead === 'qr' && tmpSegments.length >= 2)
		{
			return this._entity('Sample', 'qr', tmpSegments[1], 'legacy', 'LegacyLinks', tmpEnvironment, pRaw);
		}

		if (tmpHead === 'document' && tmpSegments.length === 2 && /^\d+$/.test(tmpSegments[1]))
		{
			return this._entity('Document', 'id', tmpSegments[1], 'legacy', 'LegacyDocumentLink', tmpEnvironment, pRaw);
		}

		const tmpDefinition = this.registry.getEntity(tmpSegments[0]);
		if (tmpDefinition && tmpSegments.length >= 2)
		{
			const tmpIsAlias = tmpDefinition.Alias.toLowerCase() === tmpHead;
			if (tmpSegments.length >= 3)
			{
				const tmpKey = tmpSegments[1].toLowerCase();
				if (tmpKey === 'qr')
				{
					return this._entity(tmpDefinition.Entity, 'qr', tmpSegments[2], 1, tmpIsAlias ? 'Compact' : 'Full', tmpEnvironment, pRaw);
				}
				if (tmpKey === 'guid' || tmpKey === 'g')
				{
					return this._entity(tmpDefinition.Entity, 'guid', tmpSegments[2], 1, tmpIsAlias ? 'Compact' : 'Full', tmpEnvironment, pRaw);
				}
			}
			if (tmpSegments.length === 2)
			{
				return this._entity(tmpDefinition.Entity, tmpDefinition.Key, tmpSegments[1], 1, tmpIsAlias ? 'Compact' : 'Full', tmpEnvironment, pRaw);
			}
		}

		return this._raw(pRaw, 'ForeignURL');
	}

	/**
	 * @param {Array<string>} pSegments - Path segments after `action`.
	 * @param {string} pRaw
	 * @return {Decoded}
	 */
	_decodeActionSegments(pSegments, pRaw)
	{
		const tmpSegments = pSegments.filter((pSegment) => pSegment.length > 0).map((pSegment) => safeDecodeSegment(pSegment));
		if (tmpSegments.length === 0)
		{
			return this._raw(pRaw, 'Raw');
		}
		const tmpHead = tmpSegments[0].toLowerCase();
		if (LEGACY_ACTION_HEADS.includes(tmpHead))
		{
			const tmpLegacyKey = tmpSegments.map((pSegment) => pSegment.toLowerCase()).join('/');
			const tmpLegacy = LEGACY_ACTIONS[tmpLegacyKey] || LEGACY_ACTIONS[tmpHead];
			if (tmpLegacy)
			{
				return { Kind: 'Action', Workflow: tmpLegacy.Workflow, Verb: tmpLegacy.Verb, Arguments: [], Legacy: true, Raw: pRaw };
			}
		}
		if (tmpSegments.length < 2)
		{
			return { Kind: 'Action', Workflow: tmpHead, Verb: '', Arguments: [], Legacy: false, Raw: pRaw };
		}
		return { Kind: 'Action', Workflow: tmpHead, Verb: tmpSegments[1].toLowerCase(), Arguments: tmpSegments.slice(2), Legacy: false, Raw: pRaw };
	}

	/**
	 * @return {DecodedEntity}
	 */
	_entity(pEntity, pKey, pValue, pVersion, pShape, pEnvironment, pRaw)
	{
		return { Kind: 'Entity', Entity: pEntity, Key: pKey, Value: pValue, Version: pVersion, Shape: pShape, Environment: pEnvironment, Raw: pRaw };
	}

	/**
	 * @return {DecodedEntity}
	 */
	_raw(pRaw, pShape)
	{
		return { Kind: 'Entity', Entity: null, Key: 'qr', Value: pRaw, Version: 'raw', Shape: pShape, Raw: pRaw };
	}

	/**
	 * Build the payload to put in a QR image or a link.
	 *
	 * @param {Object} pTarget
	 * @param {string} pTarget.Entity - Registry entity name or alias.
	 * @param {string} pTarget.Value - The stored code or GUID.
	 * @param {'qr'|'guid'} [pTarget.Key] - Defaults to the entity's key.
	 * @param {Object} [pOptions]
	 * @param {Envelope} [pOptions.Envelope] - Defaults to Compact.
	 * @param {string} [pOptions.Environment] - Environment name for the links host; defaults to Production.
	 * @param {boolean} [pOptions.Uppercase] - Compact only: emit the whole URL in uppercase when that is lossless and fits alphanumeric mode. Defaults to true.
	 * @return {string}
	 */
	encode(pTarget, pOptions)
	{
		const tmpOptions = Object.assign({ Envelope: 'Compact', Environment: 'Production', Uppercase: true }, pOptions || {});
		const tmpDefinition = this.registry.getEntity(pTarget && pTarget.Entity);
		if (!tmpDefinition)
		{
			throw new Error(`Unknown entity "${pTarget && pTarget.Entity}".`);
		}
		const tmpValue = String((pTarget && pTarget.Value) ?? '');
		if (!tmpValue)
		{
			throw new Error('Cannot encode an empty value.');
		}
		const tmpKey = pTarget.Key || tmpDefinition.Key;
		const tmpEncodedValue = encodeURIComponent(tmpValue);
		const tmpLinks = libHosts.getLinksBaseURL(tmpOptions.Environment);

		switch (tmpOptions.Envelope)
		{
			case 'Raw':
				// A bare value reads back as a sample code (DefaultRawEntities), so only a sample code goes out bare; anything else keeps its links address.
				if (tmpDefinition.Entity === 'Sample' && tmpKey === 'qr')
				{
					return tmpValue;
				}
				return `${tmpLinks}/${tmpDefinition.Entity}/${tmpKey}/${tmpEncodedValue}`;
			case 'LegacyLinks':
				if (tmpKey === 'qr' && tmpDefinition.Entity === 'Sample')
				{
					return `${tmpLinks}/qr/${tmpEncodedValue}`;
				}
				return `${tmpLinks}/${tmpDefinition.Entity}/${tmpKey}/${tmpEncodedValue}`;
			case 'Full':
				return `${tmpLinks}/${tmpDefinition.Entity}/${tmpKey}/${tmpEncodedValue}`;
			case 'Compact':
			{
				const tmpKeySegment = (tmpKey === tmpDefinition.Key) ? '' : `${tmpKey === 'guid' ? 'g' : tmpKey}/`;
				const tmpPayload = `${tmpLinks}/${tmpDefinition.Alias}/${tmpKeySegment}${tmpEncodedValue}`;
				const tmpUpper = tmpPayload.toUpperCase();
				if (tmpOptions.Uppercase && tmpValue === tmpValue.toUpperCase() && isAlphanumericMode(tmpUpper))
				{
					return tmpUpper;
				}
				return tmpPayload;
			}
			default:
				throw new Error(`Unknown envelope "${tmpOptions.Envelope}".`);
		}
	}

	/**
	 * Build an action-card payload: `action/<workflow>/<verb>[/<arg>…]`.
	 *
	 * @param {string} pWorkflow
	 * @param {string} pVerb
	 * @param {Array<string>} [pArguments]
	 * @return {string}
	 */
	encodeAction(pWorkflow, pVerb, pArguments)
	{
		const tmpWorkflow = String(pWorkflow || '').toLowerCase();
		if (!tmpWorkflow || LEGACY_ACTION_HEADS.includes(tmpWorkflow))
		{
			throw new Error(`"${pWorkflow}" can't be used as a workflow name.`);
		}
		const tmpParts = [ 'action', tmpWorkflow, String(pVerb || '').toLowerCase() ].concat((pArguments || []).map((pArgument) => encodeURIComponent(String(pArgument))));
		return tmpParts.join('/');
	}
}

module.exports = QRCodeCodec;
module.exports.cleanScannedText = cleanScannedText;
module.exports.isAlphanumericMode = isAlphanumericMode;
module.exports.LEGACY_ACTIONS = LEGACY_ACTIONS;
